import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes, FaSave, FaSpinner, FaUpload, FaTrash } from 'react-icons/fa';
import { TrainingPayment, TrainingRequest } from '../types';
import type { SelectOption } from '../../../utils/select-with-search';
import { SearchableSelect } from '../../../utils/select-with-search';
import api from '../../../libs/axios';
import toast from 'react-hot-toast';

interface EditPaymentOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  payment: TrainingPayment | null;
  loading?: boolean;
}

export const EditPaymentOffcanvas: React.FC<EditPaymentOffcanvasProps> = ({ isOpen, onClose, onSubmit, payment, loading = false }) => {
  const [formData, setFormData] = useState({
    pi_number: '', pi_date: '',
    invoice_number: '', invoice_date: '', pi_basic_amount: '', pi_tax_amount: '',
    pi_total_amount: '', invoice_basic_amount: '', invoice_tax_amount: '', invoice_total_amount: '',
    per_person_amount: '', no_of_candidates: '',
  });
  const [selectedPiRequests, setSelectedPiRequests] = useState<SelectOption[]>([]);
  const [availablePiRequests, setAvailablePiRequests] = useState<SelectOption[]>([]);
  const [availableInvoiceRequests, setAvailableInvoiceRequests] = useState<SelectOption[]>([]);
  const [selectedRequests, setSelectedRequests] = useState<SelectOption[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);
  const [invoiceDocuments, setInvoiceDocuments] = useState<File[]>([]);
  const [pibDocuments, setPibDocuments] = useState<File[]>([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);

  useEffect(() => {
    if (payment && isOpen) {
      setFormData({
        pi_number: (payment as any).pi_number || '',
        pi_date: (payment as any).pi_date ? (payment as any).pi_date.split('T')[0] : '',
        invoice_number: payment.invoice_number || '',
        invoice_date: payment.invoice_date ? payment.invoice_date.split('T')[0] : '',
        pi_basic_amount: payment.pi_basic_amount?.toString() || '',
        pi_tax_amount: payment.pi_tax_amount?.toString() || '',
        pi_total_amount: payment.pi_total_amount?.toString() || '',
        invoice_basic_amount: (payment as any).invoice_basic_amount?.toString() || '',
        invoice_tax_amount: (payment as any).invoice_tax_amount?.toString() || '',
        invoice_total_amount: (payment as any).invoice_total_amount?.toString() || '',
        per_person_amount: payment.per_person_amount?.toString() || '',
        no_of_candidates: payment.no_of_candidates?.toString() || '',
      });
      setSelectedPiRequests([]);
      setSelectedRequests([]);
      setUploadedDocuments([]);
      setInvoiceDocuments([]);
      setPibDocuments([]);
    }
  }, [payment, isOpen]);

  useEffect(() => {
    if (!payment || !isOpen) return;
    let cancelled = false;
    setUploadingDocs(true);
    api.get(`/training/payments/${payment.id}/documents`)
      .then((res) => {
        if (!cancelled && res.data.success) {
          setUploadedDocuments(res.data.data?.documents || []);
        }
      })
      .catch(() => {
        if (!cancelled) setUploadedDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setUploadingDocs(false);
      });
    return () => { cancelled = true; };
  }, [payment, isOpen]);

  // Fetch available requests when payment changes
  useEffect(() => {
    if (!payment || !isOpen) return;

    let cancelled = false;
    const fetchRequests = async () => {
      setRequestsLoading(true);
      try {
        const res = await api.get("/training/payments/candidates/available", {
          params: {
            training_institute_id: payment.training_institute_id,
            training_type_id: payment.training_type_id,
            payment_id: payment.id,
          },
        });
        if (!cancelled && res.data.success) {
          const toOption = (r: any): SelectOption => ({
            id: r.id, value: r.id,
            label: r.request_id ? `${r.request_id}${r.candidate_name ? ` - ${r.candidate_name}` : ""}` : `Request #${r.id}`,
          });

          const rawPi: any[] = res.data.data?.pi_candidates ?? [];
          const rawInvoice: any[] = res.data.data?.invoice_candidates ?? [];

          // Build maps for lookup
          const piOptionById = new Map<number, SelectOption>(rawPi.map((r: any) => [Number(r.id), toOption(r)]));
          const invoiceOptionById = new Map<number, SelectOption>(rawInvoice.map((r: any) => [Number(r.id), toOption(r)]));

          // Resolve selected PI requests — use api data if available, else fall back to payment data
          const piIds: number[] = ((payment as any).pi_candidate_ids || []).map(Number);
          const piSelected: SelectOption[] = piIds.map(id => {
            if (piOptionById.has(id)) return piOptionById.get(id)!;
            // fallback: build from pi_training_requests on the payment
            const piReqs: any[] = (payment as any).pi_training_requests || [];
            const found = piReqs.find((r: any) => Number(r.id) === id);
            return found ? toOption(found) : { id, value: id, label: `Request #${id}` };
          });
          if (!cancelled) setSelectedPiRequests(piSelected);

          // Resolve selected Invoice requests
          const invoiceSelected: SelectOption[] = (payment.payment_candidates || [])
            .map(pc => {
              const r = pc.training_request;
              if (r) return toOption(r as any);
              const id = Number(pc.training_request_id);
              return invoiceOptionById.get(id) ?? { id, value: id, label: `Request #${id}` };
            });
          if (!cancelled) setSelectedRequests(invoiceSelected);

          // PI dropdown options = returned pi_candidates (already excludes other payments' PI; current payment's PI included)
          if (!cancelled) setAvailablePiRequests(rawPi.map(toOption));
          // Invoice dropdown options = returned invoice_candidates
          if (!cancelled) setAvailableInvoiceRequests(rawInvoice.map(toOption));
        }
      } catch (error) {
        if (!cancelled) { setAvailablePiRequests([]); setAvailableInvoiceRequests([]); }
      } finally {
        if (!cancelled) setRequestsLoading(false);
      }
    };

    fetchRequests();
    return () => { cancelled = true; };
  }, [payment, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRequestSelectChange = (options: SelectOption[] = []) => {
    setSelectedRequests(options);
  };

  const handlePiRequestSelectChange = (options: SelectOption[] = []) => {
    setSelectedPiRequests(options);
  };

  const handleDocumentSelect = (files: FileList | null, setter: React.Dispatch<React.SetStateAction<File[]>>) => {
    if (!files) return;
    setter(prev => [...prev, ...Array.from(files)]);
  };

  const handleDeleteUploadedDocument = async (docId: number) => {
    try {
      await api.delete(`/training/payments/documents/${docId}`);
      setUploadedDocuments(prev => prev.filter((doc: any) => doc.id !== docId));
      toast.success("Document removed");
    } catch {
      toast.error("Failed to remove document");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!payment) return;
    
    const submitData: any = { id: payment.id };
    if (formData.pi_number) submitData.pi_number = formData.pi_number;
    if (formData.pi_date) submitData.pi_date = formData.pi_date;
    if (formData.invoice_number) submitData.invoice_number = formData.invoice_number;
    if (formData.invoice_date) submitData.invoice_date = formData.invoice_date;
    if (formData.pi_basic_amount) submitData.pi_basic_amount = Number(formData.pi_basic_amount);
    if (formData.pi_tax_amount) submitData.pi_tax_amount = Number(formData.pi_tax_amount);
    if (formData.pi_total_amount) submitData.pi_total_amount = Number(formData.pi_total_amount);
    if (formData.invoice_basic_amount) submitData.invoice_basic_amount = Number(formData.invoice_basic_amount);
    if (formData.invoice_tax_amount) submitData.invoice_tax_amount = Number(formData.invoice_tax_amount);
    if (formData.invoice_total_amount) submitData.invoice_total_amount = Number(formData.invoice_total_amount);
    if (formData.per_person_amount) submitData.per_person_amount = Number(formData.per_person_amount);
    if (formData.no_of_candidates) submitData.no_of_candidates = Number(formData.no_of_candidates);

    submitData.candidate_ids = selectedRequests.map(r => Number(r.value)).filter(id => !isNaN(id));
    submitData.pi_candidate_ids = selectedPiRequests.map(r => Number(r.value)).filter(id => !isNaN(id));
    submitData._payment_documents = [
      ...invoiceDocuments.map((file) => ({ file, document_type: "invoice" })),
      ...pibDocuments.map((file) => ({ file, document_type: "pib" })),
    ];
    
    onSubmit(submitData);
  };

  const renderDocumentPicker = (
    label: string,
    files: File[],
    setter: React.Dispatch<React.SetStateAction<File[]>>
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <label className="cursor-pointer flex flex-col items-center gap-2 border-2 border-dashed border-gray-300 rounded p-4 text-center">
        <FaUpload className="w-5 h-5 text-gray-400" />
        <span className="text-sm text-gray-600">{files.length > 0 ? `${files.length} file(s) selected` : "Click to upload documents"}</span>
        <span className="text-xs text-gray-500">PDF, DOC, XLS, JPG, PNG up to 50MB</span>
        <input
          type="file"
          multiple
          onChange={(e) => {
            handleDocumentSelect(e.target.files, setter);
            e.target.value = "";
          }}
          disabled={loading || uploadingDocs}
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
        />
      </label>
      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {files.map((doc, idx) => (
            <div key={`${doc.name}-${idx}`} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded text-sm">
              <span className="text-blue-900 truncate">{doc.name}</span>
              <button type="button" onClick={() => setter(prev => prev.filter((_, i) => i !== idx))} disabled={loading || uploadingDocs} className="text-red-600 hover:text-red-800 disabled:opacity-50">
                <FaTrash className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen && !loading) onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  if (!isOpen || !payment) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!loading ? onClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Edit Payment</h2>
            <p className="text-sm text-gray-600 mt-1">Update payment details.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded" disabled={loading}><FaTimes className="w-5 h-5 text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Institute:</span><p className="font-medium">{payment.training_institute?.institute_name || "-"}</p></div>
              <div><span className="text-gray-500">Type:</span><p className="font-medium">{payment.training_type?.training_name || "-"}</p></div>
              <div><span className="text-gray-500">Payment Type:</span><p className="font-medium capitalize">{payment.payment_type.replace('_', ' ')}</p></div>
              <div><span className="text-gray-500">Status:</span><p className="font-medium capitalize">{payment.payment_status}</p></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* PI Fields */}
            <div className="border border-gray-200 rounded p-4 space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">PI Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Number</label>
                  <input type="text" name="pi_number" value={formData.pi_number} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="PI-2026-001" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Date</label>
                  <input type="date" name="pi_date" value={formData.pi_date} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Basic Amount</label>
                  <input type="number" name="pi_basic_amount" value={formData.pi_basic_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Tax Amount</label>
                  <input type="number" name="pi_tax_amount" value={formData.pi_tax_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Total Amount</label>
                  <input type="number" name="pi_total_amount" value={formData.pi_total_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" step="0.01" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PI Training IDs</label>
                <SearchableSelect
                  value={selectedPiRequests}
                  onChange={handlePiRequestSelectChange}
                  options={availablePiRequests}
                  isLoading={requestsLoading}
                  placeholder={requestsLoading ? "Loading requests..." : "Search by request ID or candidate name"}
                  disabled={loading}
                  isMulti={true}
                  closeOnOutsideClick={false}
                  minSearchLength={0}
                  className="w-full"
                />
                <p className="mt-1 text-xs text-gray-500">Select one or more training requests for PI</p>
              </div>
            </div>

            {/* Invoice Fields */}
            <div className="border border-gray-200 rounded p-4 space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input type="text" name="invoice_number" value={formData.invoice_number} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="INV-2026-001" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input type="date" name="invoice_date" value={formData.invoice_date} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Basic Amount</label>
                  <input type="number" name="invoice_basic_amount" value={formData.invoice_basic_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Tax Amount</label>
                  <input type="number" name="invoice_tax_amount" value={formData.invoice_tax_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Total Amount</label>
                  <input type="number" name="invoice_total_amount" value={formData.invoice_total_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" step="0.01" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Training IDs</label>
                <SearchableSelect
                  value={selectedRequests}
                  onChange={handleRequestSelectChange}
                  options={availableInvoiceRequests}
                  isLoading={requestsLoading}
                  placeholder={requestsLoading ? "Loading requests..." : "Search by request ID or candidate name"}
                  disabled={loading}
                  isMulti={true}
                  closeOnOutsideClick={false}
                  minSearchLength={0}
                  className="w-full"
                />
                <p className="mt-1 text-xs text-gray-500">Select one or more training requests for invoice</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Per Person Amount</label>
                <input type="number" name="per_person_amount" value={formData.per_person_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" step="0.01" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">No. of Candidates</label>
                <input type="number" name="no_of_candidates" value={formData.no_of_candidates} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" min="1" />
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Documents</p>
              {renderDocumentPicker("Invoice Documents", invoiceDocuments, setInvoiceDocuments)}
              {renderDocumentPicker("PIB Documents", pibDocuments, setPibDocuments)}

              {uploadedDocuments.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-medium text-gray-700">Uploaded Documents:</p>
                  {uploadedDocuments.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                      <a href={doc.document_url} target="_blank" rel="noopener noreferrer" className="text-green-900 hover:underline truncate">
                        {doc.document_name}
                        <span className="ml-2 text-xs text-green-700 capitalize">({doc.document_type?.replace(/_/g, " ")})</span>
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDeleteUploadedDocument(doc.id)}
                        disabled={loading || uploadingDocs}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} disabled={loading || uploadingDocs} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={handleSubmit} disabled={loading || uploadingDocs} className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 disabled:opacity-50 flex items-center gap-2">
              {loading || uploadingDocs ? (<><FaSpinner className="w-4 h-4 animate-spin" />Updating...</>) : (<><FaSave className="w-4 h-4" />Update Payment</>)}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
